<template>
  <div class="modal-card" style="width: auto">
    <b-loading
      :is-full-page="true"
      :active="isLoading"
      :can-cancel="false"
    ></b-loading>

    <header class="modal-card-head">
      <p class="modal-card-title has-text-centered">
        {{ $t("settings.allowList") }}
      </p>
    </header>

    <section class="modal-card-body">
      <p v-html="$t('settings.allowListInstruction1')"></p>
      <p v-html="$t('settings.allowListInstruction2')"></p>
      <p v-html="$t('settings.allowListInstruction3')"></p>
      <p v-html="$t('settings.allowListInstruction4')"></p>
      <p v-html="$t('settings.allowListInstruction5')"></p>

      <hr />

      <b-field :label="$t('settings.email')" label-position="on-border">
        <b-input
          v-model="email"
          placeholder="email@email.com"
          @keyup.enter.native="addEmail"
        ></b-input>

        <p class="control">
          <button
            class="button"
            :style="{
              backgroundColor: $primaryColor,
              color: $primaryColorContrast
            }"
            @click="addEmail"
          >
            +
          </button>
        </p>
      </b-field>

      <p>
        <b> {{ `${$t("settings.emailList")} (${this.emailList.length})` }}: </b>
        <span
          v-for="p in filteredEmailList"
          :key="p"
          class="button is-rounded is-small"
        >
          {{ p }}
          &nbsp;&nbsp;&nbsp;
          <span @click="removeEmail(p)">
            <b-icon
              icon="times-circle"
              size="is-medium"
              type="is-dark"
              style="position: relative; top: 2px;"
            ></b-icon>
          </span>
        </span>
      </p>

      <hr />

      <b-field :label="$t('settings.cellphone')" label-position="on-border">
        <vue-tel-input
          v-model="cellphone"
          :inputOptions="{
            placeholder: $t('settings.cellphoneWithAreaCode')
          }"
          :dropdownOptions="{
            showDialCodeInList: true,
            showDialCodeInSelection: true,
            showFlags: true
          }"
          validCharactersOnly
          @country-changed="countryChanged"
          @validate="vueTelValidate"
          :defaultCountry="defaultCountry"
          :preferredCountries="preferredCountries"
          @keyup.enter.native="addCellphone"
          @open="changeDropdownZIndex"
        ></vue-tel-input>

        <p class="control">
          <button
            class="button"
            :style="{
              backgroundColor: $primaryColor,
              color: $primaryColorContrast
            }"
            @click="addCellphone"
          >
            +
          </button>
        </p>
      </b-field>

      <p>
        <b>
          {{
            `${$t("settings.cellphoneList")} (${this.cellphoneList.length})`
          }}:
        </b>
        <span
          v-for="p in filteredCellphoneList"
          :key="p"
          class="button is-rounded is-small"
        >
          {{ p }}
          &nbsp;&nbsp;&nbsp;
          <span @click="removeCellphone(p)">
            <b-icon
              icon="times-circle"
              size="is-medium"
              type="is-dark"
              style="position: relative; top: 2px;"
            ></b-icon>
          </span>
        </span>
      </p>

      <hr />

      <b-field :label="$t('settings.personalId')" label-position="on-border">
        <b-input
          v-model="personalId"
          placeholder="123.456.789-10"
          @input.native="personalId = formatPersonalId('personalId')"
          @keyup.enter.native="addPersonalId"
        ></b-input>

        <p class="control">
          <button
            class="button"
            :style="{
              backgroundColor: $primaryColor,
              color: $primaryColorContrast
            }"
            @click="addPersonalId"
          >
            +
          </button>
        </p>
      </b-field>

      <p>
        <b>
          {{
            `${$t("settings.personalIdList")} (${this.personalIdList.length})`
          }}:
        </b>
        <span
          v-for="p in filteredPersonalIdList"
          :key="p"
          class="button is-rounded is-small"
        >
          {{ p }}
          &nbsp;&nbsp;&nbsp;
          <span @click="removePersonalId(p)">
            <b-icon
              icon="times-circle"
              size="is-medium"
              type="is-dark"
              style="position: relative; top: 2px;"
            ></b-icon>
          </span>
        </span>
      </p>
    </section>

    <footer class="modal-card-foot" style="justify-content: space-between;">
      <span>
        <button class="button" type="button" @click="$parent.close()">
          {{ $t("common.cancel") }}
        </button>

        <button
          class="button"
          :style="{
            backgroundColor: $primaryColor,
            color: $primaryColorContrast
          }"
          type="button"
          @click="updateAllowList"
        >
          {{ $t("settings.update") }}
        </button>
      </span>
    </footer>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { common } from "../../mixins/common";
import { mask } from "../../mixins/mask";

export default {
  mixins: [common, mask],

  data() {
    return {
      isLoading: false,

      personalId: "",
      personalIdList: [],
      filteredPersonalIdList: [],

      email: "",
      emailList: [],
      filteredEmailList: [],

      countryCallingCode: "",
      cellphone: "",
      cellphoneList: [],
      filteredCellphoneList: [],

      defaultCountry: "BR",
      preferredCountries: ["BR", "PT", "ES"],
      isTelValid: false
    };
  },

  methods: {
    addPersonalId() {
      if (this.personalIdList.includes(this.personalId)) {
        this.$buefy.toast.open({
          message: this.$t("settings.personalIdAlreadyRegistered"),
          type: "is-danger",
          position: "is-bottom",
          duration: 2000
        });
      } else if (!this.isPersonalIdValid(this.personalId)) {
        this.$buefy.toast.open({
          message: this.$t("settings.invalidPersonalId"),
          type: "is-danger",
          position: "is-bottom",
          duration: 2000
        });
      } else {
        this.personalIdList.push(this.personalId);
        this.personalId = "";
      }
    },

    removePersonalId(id) {
      let index = this.personalIdList.findIndex(p => {
        return p === id;
      });
      if (index !== -1) this.personalIdList.splice(index, 1);
    },

    addEmail() {
      if (this.emailList.includes(this.email)) {
        this.$buefy.toast.open({
          message: this.$t("settings.emailAlreadyRegistered"),
          type: "is-danger",
          position: "is-bottom",
          duration: 2000
        });
      } else if (!this.isEmailValid(this.email)) {
        this.$buefy.toast.open({
          message: this.$t("settings.invalidEmail"),
          type: "is-danger",
          position: "is-bottom",
          duration: 2000
        });
      } else {
        this.emailList.push(this.email);
        this.email = "";
      }
    },

    removeEmail(email) {
      let index = this.emailList.findIndex(p => {
        return p === email;
      });
      if (index !== -1) this.emailList.splice(index, 1);
    },

    vueTelValidate(t) {
      this.countryCallingCode = t.country.dialCode;
      this.isTelValid = t.valid;
    },

    countryChanged(c) {
      this.countryCallingCode = c.dialCode;
    },

    changeDropdownZIndex() {
      setTimeout(() => {
        document.getElementsByClassName("vti__dropdown-list")[0].style.zIndex =
          "10";
      }, 1);
    },

    addCellphone() {
      if (!this.isTelValid) {
        this.$buefy.toast.open({
          message: this.$t("settings.invalidCellphone"),
          type: "is-danger",
          position: "is-bottom",
          duration: 2000
        });
      } else if (
        this.cellphoneList.includes(
          this.countryCallingCode.concat(this.cellphone.replace(/\D/g, ""))
        )
      ) {
        this.$buefy.toast.open({
          message: this.$t("settings.cellphoneAlreadyRegistered"),
          type: "is-danger",
          position: "is-bottom",
          duration: 2000
        });
      } else {
        this.cellphoneList.push(
          this.countryCallingCode.concat(this.cellphone.replace(/\D/g, ""))
        );
        this.cellphone = "";
        this.isTelValid = false;
      }
    },

    removeCellphone(cellphone) {
      let index = this.cellphoneList.findIndex(p => {
        return p === cellphone;
      });
      if (index !== -1) this.cellphoneList.splice(index, 1);

      index = this.filteredCellphoneList.findIndex(p => {
        return p === cellphone;
      });
      if (index !== -1) this.filteredCellphoneList.splice(index, 1);
    },

    updateAllowList() {
      this.isLoading = true;
      this.$store
        .dispatch("updateAllowList", {
          storeId: this.storeBeingManaged._id,
          emailList: this.emailList,
          cellphoneList: this.cellphoneList,
          personalIdList: this.personalIdList
        })
        .then(res => {
          if (res.data.result === "ok") {
            this.$buefy.toast.open({
              message: this.$t("settings.allowListUpdated"),
              type: "is-success",
              position: "is-bottom",
              duration: 5000
            });
            this.$parent.close();
          } else {
            this.alertError(res.data);
          }
        })
        .catch(() => {
          this.alertError({ message: "UNEXPECTED_ERROR" });
        })
        .finally(() => {
          this.isLoading = false;
        });
    }
  },

  computed: {
    ...mapGetters(["storeBeingManaged"])
  },

  watch: {
    personalId() {
      this.filteredPersonalIdList = this.personalIdList.filter(p =>
        p.match(this.personalId)
      );
    },

    email() {
      this.filteredEmailList = this.emailList.filter(e => e.match(this.email));
    },

    cellphone() {
      this.filteredCellphoneList = this.cellphoneList.filter(c =>
        c.match(
          this.countryCallingCode.concat(this.cellphone.replace(/\D/g, ""))
        )
      );
    },

    countryCallingCode() {
      this.filteredCellphoneList = this.cellphoneList.filter(c =>
        c.match(
          this.countryCallingCode.concat(this.cellphone.replace(/\D/g, ""))
        )
      );
    }
  },

  mounted() {
    this.emailList = this.storeBeingManaged.configs.allowList.emails;
    this.filteredEmailList = this.emailList;

    this.cellphoneList = this.storeBeingManaged.configs.allowList.whatsappId;
    this.filteredCellphoneList = this.cellphoneList;

    this.personalIdList = this.storeBeingManaged.configs.allowList.personalId;
    this.filteredPersonalIdList = this.personalIdList;
  }
};
</script>

<style scoped>
.vti__dropdown-list {
  z-index: 10;
}
</style>
